import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';

export enum NonprofitFundingSourcesOptions {
  FUNDRAISING = 'FUNDRAISING',
  PRIVATE_CONTRIBUTIONS = 'PRIVATE_CONTRIBUTIONS',
  CORPORATE_SPONSORSHIPS = 'CORPORATE_SPONSORSHIPS',
  GOVERNMENT_GRANTS = 'GOVERNMENT_GRANTS',
  MEMBERSHIP_DUES = 'MEMBERSHIP_DUES',
  COMMUNITY_SUPPORT = 'COMMUNITY_SUPPORT',
  OTHER = 'OTHER',
}

export const useNonprofitFundingSourcesLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const optionsList = [
    {
      value: NonprofitFundingSourcesOptions.FUNDRAISING,
      getLabel: () => t('organic.nonprofitFundingSources.fundraising'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER, // Only OTHER needs a text field
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.PRIVATE_CONTRIBUTIONS,
      getLabel: () => t('organic.nonprofitFundingSources.privateContributions'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.CORPORATE_SPONSORSHIPS,
      getLabel: () => t('organic.nonprofitFundingSources.corporateSponsorships'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.GOVERNMENT_GRANTS,
      getLabel: () => t('organic.nonprofitFundingSources.governmentGrants'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.MEMBERSHIP_DUES,
      getLabel: () => t('organic.nonprofitFundingSources.membershipDues'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.COMMUNITY_SUPPORT,
      getLabel: () => t('organic.nonprofitFundingSources.communitySupport'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
    {
      value: NonprofitFundingSourcesOptions.OTHER,
      getLabel: () => t('organic.nonprofitFundingSources.other'),
      field: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
      placeholder: t('organic.nonprofitFundingSources.otherPlaceholder'),
    },
  ];

  const formSchema = z
    .object({
      [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED]: z
        .array(z.string())
        .min(1, { message: tValidation('validation.required') }),
      [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const selectedOptions = data[
        EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED
      ] as string[];

      if (selectedOptions.includes(NonprofitFundingSourcesOptions.OTHER)) {
        const value = data[EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER] as string | undefined;
        if (!value || value.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.required'),
            path: [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER],
          });
        } else if (value.length < 30) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.min30chars'),
            path: [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER],
          });
        } else if (value.length > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tValidation('validation.max100chars'),
            path: [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER],
          });
        }
      }
    });

  const formDefaultValues = {
    [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED]: (() => {
      const value = getDefaultValueByQuestionId(
        EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED
      );
      if (!Array.isArray(value)) return [];
      return value;
    })(),
    [EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER) || '',
  };

  const formMethods = useFormHook({
    schemaTitle: 'Non-profit Funding Sources',
    formSchema,
    formDefaultValues,
  });

  const handleSubmit = async () => {
    const selectedOptions = JSON.stringify(
      formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED) as string[]
    );

    saveEddQuestionToLocalStore({
      question: t('organic.nonprofitFundingSources.instruction'),
      questionId: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED,
      answer: selectedOptions,
    });

    const selectedOptionsArray = formMethods.getValues(
      EddEmbeddedFields.EDD_NONPROFIT_FUNDING_SOURCES_SELECTED
    ) as string[];
    if (selectedOptionsArray.includes(NonprofitFundingSourcesOptions.OTHER)) {
      const otherValue = formMethods.getValues(EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER);
      if (otherValue) {
        saveEddQuestionToLocalStore({
          question: `Free Text Of - ${t('organic.nonprofitFundingSources.instruction')}`,
          questionId: EddEmbeddedFields.EDD_NONPROFIT_FUNDING_OTHER,
          answer: JSON.stringify(otherValue),
        });
      }
    }

    // Submit to server
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_NONPROFIT_FUNDING_SOURCES);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
    optionsList,
  };
};
